/**
 * MedMic proactive + 401 refresh engine (Phase 3).
 * Coordinator is the only caller; extension never acquires tokens.
 */

import { acquireCreAccessTokenSilent } from "./creAuthConfig";
import {
  MEDMIC_REFRESH_LEASE_KEY,
} from "./creExtensionConstants";
import { getCreMsalInstance, initializeCreMsal } from "./creMsalInstance";
import { getSafeTokenMeta, logMedmicAuthDiag } from "./creTokenDiagnostics";
import { getCreAccounts } from "./creAccountUtils";
import { validateMedmicToken } from "./validateMedmicToken";

export const REFRESH_TIMEOUT_MS = 25_000;
export const REFRESH_LEASE_MS = 30_000;
export const PROACTIVE_SKEW_MS = 5 * 60 * 1000;
export const MIN_REFRESH_DELAY_MS = 15_000;
export const MAX_TIMER_DELAY_MS = 24 * 60 * 60 * 1000;
export const TEMP_REFRESH_RETRY_DELAY_MS = 60_000;

/**
 * @param {number | null | undefined} expiresAt
 * @param {number} [now]
 */
export function computeProactiveRefreshDelayMs(expiresAt, now = Date.now()) {
  if (typeof expiresAt !== "number" || !Number.isFinite(expiresAt)) {
    return MIN_REFRESH_DELAY_MS;
  }
  const target = expiresAt - PROACTIVE_SKEW_MS;
  const delay = target - now;
  if (delay <= 0) return Math.min(MIN_REFRESH_DELAY_MS, 5_000);
  return Math.min(Math.max(delay, MIN_REFRESH_DELAY_MS), MAX_TIMER_DELAY_MS);
}

export function isTokenExpired(expiresAt, now = Date.now()) {
  return typeof expiresAt === "number" && now >= expiresAt;
}

/**
 * @param {string} tabId
 * @param {{ operationId: string, sessionId: string, epoch: number, generation: number }} op
 * @returns {boolean} whether this tab owns the lease
 */
export function tryClaimRefreshLease(tabId, op) {
  if (typeof localStorage === "undefined") return true;
  try {
    const raw = localStorage.getItem(MEDMIC_REFRESH_LEASE_KEY);
    const now = Date.now();
    if (raw) {
      const lease = JSON.parse(raw);
      if (
        lease &&
        lease.tabId !== tabId &&
        typeof lease.expiresAt === "number" &&
        lease.expiresAt > now &&
        lease.sessionId === op.sessionId &&
        lease.epoch === op.epoch
      ) {
        return false;
      }
    }
    localStorage.setItem(
      MEDMIC_REFRESH_LEASE_KEY,
      JSON.stringify({
        tabId,
        operationId: op.operationId,
        sessionId: op.sessionId,
        epoch: op.epoch,
        generation: op.generation,
        expiresAt: now + REFRESH_LEASE_MS,
      }),
    );
    return true;
  } catch {
    return true;
  }
}

export function releaseRefreshLease(tabId) {
  if (typeof localStorage === "undefined") return;
  try {
    const raw = localStorage.getItem(MEDMIC_REFRESH_LEASE_KEY);
    if (!raw) return;
    const lease = JSON.parse(raw);
    if (lease?.tabId === tabId) {
      localStorage.removeItem(MEDMIC_REFRESH_LEASE_KEY);
    }
  } catch {
    /* ignore */
  }
}

/** Invalidate any refresh lease (disconnect / session replacement). */
export function invalidateAllRefreshLeases() {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(MEDMIC_REFRESH_LEASE_KEY);
  } catch {
    /* ignore */
  }
}

export function clearRefreshLeaseForTests() {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(MEDMIC_REFRESH_LEASE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Resolve MedMic account for silent refresh (must match known identity).
 */
export function resolveRefreshAccount(instance, homeAccountId) {
  const accounts = getCreAccounts(instance);
  if (!homeAccountId) {
    return { account: null, reason: "no_known_medmic_account" };
  }
  const match = accounts.find((a) => a.homeAccountId === homeAccountId);
  if (!match) {
    return { account: null, reason: "account_mismatch" };
  }
  return { account: match, reason: null };
}

/**
 * Classify Validate / MSAL outcomes for refresh.
 * @param {{ ok: boolean, error?: { code?: string, message?: string } }} validateResult
 * @param {{ code?: string }} [acquireResult]
 */
export function classifyRefreshFailure(validateResult, acquireResult) {
  if (acquireResult && !acquireResult.ok) {
    if (
      acquireResult.code === "interaction_required" ||
      acquireResult.code === "authority_mismatch" ||
      acquireResult.code === "no_account" ||
      acquireResult.code === "account_mismatch" ||
      acquireResult.code === "no_known_medmic_account"
    ) {
      return {
        terminal: true,
        state: "interaction_required",
        code: acquireResult.code,
        message: acquireResult.message || acquireResult.code,
      };
    }
    return {
      terminal: false,
      state: "refresh_failed",
      code: acquireResult.code || "transient_msal_error",
      message: acquireResult.message || "Silent acquire failed.",
    };
  }

  const code = validateResult?.error?.code || "validate_failed";
  const message = validateResult?.error?.message || "Validate failed.";
  if (
    code === "validate_http_error" &&
    /HTTP 401|HTTP 403/.test(message)
  ) {
    return {
      terminal: true,
      state: "interaction_required",
      code: "validate_auth_rejected",
      message,
    };
  }
  if (code === "validate_aborted") {
    return {
      terminal: false,
      state: "refresh_failed",
      code,
      message,
    };
  }
  if (
    code === "validate_network_error" ||
    /HTTP 5\d\d/.test(message) ||
    code === "validate_http_error"
  ) {
    return {
      terminal: false,
      state: "refresh_failed",
      code,
      message,
    };
  }
  return {
    terminal: true,
    state: "validation_failed",
    code,
    message,
  };
}

/**
 * Run acquireTokenSilent + Validate for a refresh operation.
 * Caller owns commit/publish and stale-op checks around this.
 *
 * @param {{
 *   source: string,
 *   homeAccountId: string | null,
 *   signal?: AbortSignal,
 * }} args
 */
export async function acquireAndValidateRefreshCandidate(args) {
  const { source, homeAccountId, signal } = args;
  await initializeCreMsal();
  const instance = getCreMsalInstance();
  const { account, reason } = resolveRefreshAccount(instance, homeAccountId);
  if (!account) {
    return {
      ok: false,
      acquire: {
        ok: false,
        code: reason || "no_account",
        message: reason || "No matching MedMic account.",
      },
    };
  }

  const acquire = await acquireCreAccessTokenSilent(instance, account);
  if (!acquire.ok) {
    return { ok: false, acquire };
  }

  const meta = getSafeTokenMeta(acquire.accessToken);
  logMedmicAuthDiag("medmic", "refresh_candidate", {
    source,
    fingerprint: meta.fingerprint,
    aud: meta.aud,
    iat: meta.iat,
    exp: meta.exp,
    accountFingerprint: String(account.homeAccountId || "").slice(0, 12),
  });

  const validate = await validateMedmicToken(acquire.accessToken, {
    source,
    signal,
  });

  if (!validate.ok) {
    return { ok: false, acquire, validate, meta, account };
  }

  return {
    ok: true,
    accessToken: acquire.accessToken,
    user: validate.user,
    userEmail: validate.userEmail,
    meta,
    account,
  };
}
